import { useTranslation } from "react-i18next";
import { MapPin, Facebook, Instagram, Rss } from "lucide-react";
import LocalizedLink from "./LocalizedLink";
import LanguageSelector from "./LanguageSelector";

const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-muted/30 border-t mt-20">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-6 w-6 text-primary" />
              <span className="text-xl font-bold">Explaora</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('footer.description')}
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="font-semibold mb-4">{t('footer.navigation')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <LocalizedLink to="/" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.home')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/explore" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.explore')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/my-trips" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.myTrips')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/map" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('worldMap.title')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/destinations" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('destinations.breadcrumb')}
                </LocalizedLink>
              </li>
            </ul>
          </div>

          {/* Ressources */}
          <div>
            <h3 className="font-semibold mb-4">{t('footer.resources')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <LocalizedLink to="/about" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.about')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/story" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('footer.story')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/guide" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.guide')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/blog" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.blog')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/faq" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.faq')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/contact" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.contact')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/agencies" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('footer.agencies')}
                </LocalizedLink>
              </li>
            </ul>
          </div>

          {/* Contact & Social */}
          <div>
            <h3 className="font-semibold mb-4">{t('footer.followUs')}</h3>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground mb-3">
                {t('footer.socialDescription')}
              </p>
              <div className="flex gap-3">
                <a 
                  href="https://www.facebook.com/profile.php?id=61584638679383&locale=fr_FR" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
                <a 
                  href="https://www.instagram.com/explaora?igsh=cXplajE5NTh1N2ll" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
                <a 
                  href="https://dzviwetvlawkxsfwpxnp.supabase.co/functions/v1/generate-rss" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Flux RSS"
                  title="S'abonner au flux RSS du blog"
                >
                  <Rss className="h-5 w-5" />
                </a>
              </div>
              
              {/* Language Selector */}
              <div className="pt-4 border-t mt-4">
                <p className="text-sm text-muted-foreground mb-2">{t('footer.language')}</p>
                <LanguageSelector />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Explaora. {t('footer.rights')}
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <LocalizedLink to="/legal/terms" className="text-muted-foreground hover:text-foreground transition-colors">
                {t('footer.terms')}
              </LocalizedLink>
              <LocalizedLink to="/legal/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
                {t('footer.privacy')}
              </LocalizedLink>
              <LocalizedLink to="/legal/notice" className="text-muted-foreground hover:text-foreground transition-colors">
                {t('footer.legal')}
              </LocalizedLink>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
